import { useCallback, useEffect, useState } from 'react'

import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { ALL_CANADA_LOCATION_ID } from '@/constants/location'
import { isDevelopment, SHOPPING_ADS } from '@/constants/others'
import { type CasListing, useGetShoppingAdsLazyQuery } from '@/generated'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import {
  GA_SCROLL_DIRECTION,
  getShoppingAdClickLabel,
  getShoppingAdLoadedLabel,
  getShoppingAdScrollLabel,
} from '@/lib/ga/events/shoppingAd'
import { sendToLogger } from '@/utils/sendToLogger'

import { ShoppingAdsCarousel } from './ShoppingAdsCarousel'

/**
 * Shopping Ads for SRP, handles the logic to display the shopping ads carousel
 *
 * @returns the carousel component or null
 */
export const ShoppingAds = () => {
  const { data: searchData } = useGetSearchResultsData()
  const keywords = searchData?.searchQuery?.keywords || ''
  const categoryId = searchData?.searchQuery?.category?.id || ALL_CATEGORIES_ID_NUM
  const locationId = searchData?.searchQuery?.location?.id || ALL_CANADA_LOCATION_ID

  const [shoppingAds, setShoppingAdsListings] = useState<CasListing[]>([])
  const [latestIds, setLatestIds] = useState({ categoryId: -1, locationId: -1 })
  const [getShoppingAds] = useGetShoppingAdsLazyQuery()

  const getAllShoppingAds = useCallback(
    async (categoryId: number, locationId: number) => {
      if (isDevelopment) {
        const { MOCK_SHOPPING_ADS } = await import('@/graphql/srp/__mocks__/getShoppingAdsMock')

        const mockData = MOCK_SHOPPING_ADS({ limit: SHOPPING_ADS.MAX })

        setShoppingAdsListings(mockData)
        return
      }

      try {
        const { data } = await getShoppingAds({
          variables: {
            categoryId,
            location: { id: locationId },
            keywords,
            pagination: { limit: SHOPPING_ADS.MAX },
          },
        })

        if (!data?.listingShoppingAds) {
          setShoppingAdsListings([])
          return
        }

        setShoppingAdsListings(data.listingShoppingAds)
        data.listingShoppingAds.length &&
          trackEvent({
            action: GA_EVENT.ShoppingAdLoaded,
            label: getShoppingAdLoadedLabel({ listingsCount: data.listingShoppingAds.length }),
          })
      } catch (err) {
        sendToLogger(err, { fingerprint: ['ShoppingAds'] })
      }
    },
    [getShoppingAds, keywords]
  )

  useEffect(() => {
    // We don't trigger the call if the category and location hasn't change
    if (categoryId === latestIds.categoryId && locationId === latestIds.locationId) {
      return
    }

    setLatestIds({ categoryId, locationId })

    // only trigger getAllShoppingAds if categoryId and locationId are known
    if (categoryId !== -1 && locationId !== -1) {
      getAllShoppingAds(categoryId, locationId)
    }
  }, [locationId, getAllShoppingAds, latestIds.categoryId, latestIds.locationId, categoryId])

  const trackShoppingAdsClick = (id: string, pos: number) => {
    trackEvent({ action: GA_EVENT.ShoppingAdClick, label: getShoppingAdClickLabel({ id, pos }) })
  }
  const trackShoppingAdsScrollPrev = () => {
    trackEvent({
      action: GA_EVENT.ShoppingAdScroll,
      label: getShoppingAdScrollLabel({ scrollDirection: GA_SCROLL_DIRECTION.backward }),
    })
  }
  const trackShoppingAdsScrollNext = () => {
    trackEvent({
      action: GA_EVENT.ShoppingAdScroll,
      label: getShoppingAdScrollLabel({ scrollDirection: GA_SCROLL_DIRECTION.forward }),
    })
  }

  return shoppingAds.length ? (
    <ShoppingAdsCarousel
      shoppingAdsListing={shoppingAds}
      handleShoppingAdClick={trackShoppingAdsClick}
      handleShoppingAdScrollPrev={trackShoppingAdsScrollPrev}
      handleShoppingAdScrollNext={trackShoppingAdsScrollNext}
    />
  ) : null
}
